/**
 * 30 - Snatch the Scientist (10 §2): a wired research station - the laboratory, a guard house, the staff's
 * quarters - and the scientist at the laboratory door. He is taken as mission 4's officer is (a weapon in
 * his face from two metres, no alerted guard at his shoulder) and he never fights: he carries no weapon.
 * Walk him to the pickup. At full alarm (alert 3) the guards have twenty seconds to be given the order,
 * and then the nearest of them shoots him rather than lose him - and anyone walking out with him is hunted.
 */

import { EXPANSION } from '../../sim/tuning';
import type { Point } from '../types';
import {
  checker, clutter, countKind, guardAtDoor, guardsAbout, insideRect, required, ruleOf, structure, targetsOf, uniformDonor,
} from './helpers';
import { RECALL, sweepRound } from './helpers3b';
import type { ArchetypeSpec } from './types';

const ORDER_AFTER = 20;

/** Sentries on the round of the laboratory: two, and a third with a strong garrison. */
const sweepersFor = (threat: number): number => (threat >= 7 ? 3 : 2);

export const snatchScientist: ArchetypeSpec = {
  id: 'snatch-scientist',
  name: 'SNATCH THE SCIENTIST',
  nativeBiome: 'arctic',
  baseThreat: 5,
  lengthTiles: 160,
  clockScale: 1,
  startAlert: 0,
  garrison: 'regular',
  perimeter: 'wire',
  sealed: false,
  layout: 'compound',
  size: { w: [50, 58], d: [28, 32] },
  river: false,
  patrolLoop: 'inside',
  breach: { min: 0, max: 0 },

  build(g) {
    const placed: Point[] = [];
    const lab = required(g, 'laboratory', { target: true, from: placed, minDist: 0, margin: 5 });
    const house = required(g, 'guardHouse', { minDist: 14, from: placed, margin: 1.4 });
    for (let i = 0; i < 2; i++) structure(g, g.kit.shelter, { minDist: 10, from: placed, margin: 1.4 });
    structure(g, 'alarmPost', { margin: 1 });
    clutter(g, 'crate', 3);

    // He steps out for air at the laboratory door. No weapon, and nobody posted at his elbow (02 §9.1).
    const scientist = guardAtDoor(g, lab, 'officer', { captureTarget: true, captiveKind: 'scientist', unarmed: true });
    if (!scientist) g.map.problems.push('snatch-scientist: nowhere at the laboratory door for the scientist');
    guardAtDoor(g, house);
    if (!sweepRound(g, lab, sweepersFor(g.threat), g.elite ? 'elite' : 'rifleman', 6)) {
      g.map.problems.push('snatch-scientist: no room for the sentries to walk round the laboratory');
    }
    guardsAbout(g, 1 + Math.floor(g.threat / 4));

    return {
      objective: {
        kind: 'captureAndEscort', label: 'TAKE THE SCIENTIST ALIVE', targetPropIds: [lab.id], minFraction: 1,
        targetName: 'THE SCIENTIST',
        rules: [
          {
            type: 'alertConsequence', level: 3, delaySeconds: ORDER_AFTER, effect: 'shootTheCaptive',
            orders: 'TAKE HIM ALIVE AND TAKE HIM QUIETLY: AT FULL ALARM THE GUARDS ARE ORDERED TO SHOOT HIM RATHER THAN LOSE HIM',
            warning: 'THE ORDER TO SHOOT THE SCIENTIST GOES OUT IN',
            reason: 'THE SCIENTIST WAS SHOT BY HIS OWN GUARDS',
          },
          RECALL,
        ],
      },
    };
  },

  asserts(def, probe) {
    const { check, out } = checker('snatch-scientist');
    const o = def.objective;
    check(o.kind === 'captureAndEscort' && o.targetName === 'THE SCIENTIST', 'objective must be captureAndEscort, and name the scientist');
    const taken = def.spawns.filter((s) => s.captureTarget === true);
    const [man] = taken;
    check(taken.length === 1, `needs exactly one man to take, has ${taken.length}`);
    check(man?.captiveKind === 'scientist' && man.unarmed === true, 'he must be the scientist, and carry no weapon');
    check(man?.dormantUntil === undefined && man?.patrol === undefined, 'he is at the laboratory from the start');
    const [lab] = targetsOf(def, 'laboratory');
    check(lab !== undefined && countKind(def, 'laboratory') === 1, 'the one laboratory must be the target');
    if (lab && man) {
      check(insideRect(def.goalZone, lab.x, lab.z), 'the laboratory must stand inside the wire');
      check(Math.abs(man.x - lab.x) <= 7 && Math.abs(man.z - lab.z) <= 7, 'the scientist must be at the laboratory');
      check(probe.breach(lab.id) === 0 && lab.target === true, 'the laboratory must be reachable through the open gates');
      // The rule must have teeth: someone armed near enough to be given the order (alertEffects.ts).
      const reach = EXPANSION.shootRadius;
      const armed = def.spawns.filter((s) => s !== man && s.dormantUntil === undefined && s.unarmed !== true
        && s.kind !== 'miniBunker' && s.kind !== 'minitank'
        && (s.x - man.x) * (s.x - man.x) + (s.z - man.z) * (s.z - man.z) <= reach * reach);
      check(armed.length >= 1, 'an armed guard must stand near enough to shoot him');
    }
    const shoot = ruleOf(def, 'alertConsequence');
    check(
      shoot?.effect === 'shootTheCaptive' && shoot.level === 3 && shoot.delaySeconds === ORDER_AFTER,
      `alert 3 must order him shot after ${ORDER_AFTER} s`,
    );
    check(
      shoot?.orders !== undefined && shoot.warning !== undefined && shoot.reason !== undefined,
      'the rule must carry its orders, its countdown and its reason',
    );
    check(countKind(def, 'guardHouse') >= 1 && uniformDonor(def, 'guardHouse'), 'a guard must stand at the guard house: his is the uniform to take');
    return out;
  },
};
